import BaseComponent from '@/components/base'
import { prefersReducedMotion } from '@/core/utils/a11y'

const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/[]<>=+*#'

interface QueueItem {
  from: string
  to: string
  start: number
  end: number
  char?: string
}

/**
 * Decode / "scramble" text effect: each character cycles through random glyphs
 * before resolving to its final value. Adapted from the well-known TextScramble.
 */
export class Scramble {
  el: HTMLElement
  queue: QueueItem[] = []
  frame = 0
  frameRequest = 0
  onComplete?: () => void

  constructor(el: HTMLElement) {
    this.el = el
    this.update = this.update.bind(this)
  }

  play(text: string, onComplete?: () => void) {
    this.onComplete = onComplete
    const old = this.el.textContent ?? ''
    const length = Math.max(old.length, text.length)

    this.queue = []
    for (let i = 0; i < length; i++) {
      const start = Math.floor(Math.random() * 30)
      const end = start + 15 + Math.floor(Math.random() * 30)
      this.queue.push({ from: old[i] || '', to: text[i] || '', start, end })
    }

    cancelAnimationFrame(this.frameRequest)
    this.frame = 0
    this.update()
  }

  update() {
    let output = ''
    let complete = 0

    for (const item of this.queue) {
      if (this.frame >= item.end) {
        complete++
        output += item.to
      }
      else if (this.frame >= item.start) {
        if (!item.char || Math.random() < 0.28) {
          item.char = CHARS[Math.floor(Math.random() * CHARS.length)]
        }
        output += `<span class="reg-label__dud">${item.char}</span>`
      }
      else {
        output += item.from
      }
    }

    this.el.innerHTML = output

    if (complete !== this.queue.length) {
      this.frame++
      this.frameRequest = requestAnimationFrame(this.update)
    }
    else {
      this.onComplete?.()
    }
  }

  stop() {
    cancelAnimationFrame(this.frameRequest)
  }
}

/** Runs the decode effect across the registry labels on enter, with a cascade. */
export default class RegistryFrame extends BaseComponent {
  static TYPE = 'registry-frame'

  scrambles: Scramble[] = []
  timeouts: ReturnType<typeof setTimeout>[] = []

  constructor(el: HTMLElement) {
    super(el)

    if (prefersReducedMotion()) return

    const labels = this.qsa('.reg-label') as HTMLElement[]

    labels.forEach((label, i) => {
      const target = (label.textContent ?? '').trim()
      const scramble = new Scramble(label)
      this.scrambles.push(scramble)
      this.timeouts.push(setTimeout(() => scramble.play(target), i * 70))
    })
  }

  destroy() {
    this.timeouts.forEach(t => clearTimeout(t))
    this.scrambles.forEach(s => s.stop())

    super.destroy()
  }
}
